import { NewActivityData } from "./components/AddActivityDialog";
import { IssueListFilterModel, IssueListItem } from "./components/IssueList";
import { Client } from "./data/Client";
import { Issue } from "./data/Issue";
import { Timesheet } from "./data/Timesheet";
import { IssueAttach } from "./data/IssueAttach";
import { IssueComment } from "./data/IssueComment";
import { Skill } from "./data/Skill";
import { RootStore } from "./stores/RootStore"
import moment from 'moment';
import axios, { AxiosError, AxiosHeaders, AxiosResponse } from "axios"
import { IssueActivity } from "./data/IssueActivity";
import { ActivityType } from "./data/ActivityType";
import { UserType } from "./data/UserType";
import { CustomerEmployeeProfile, EmployeeProfile } from "./data/UserProfile";
import { CustomerEmployeeAccessLevel } from "./data/CustomerEmployeeAccessLevel";
import { EmployeeAccessLevel } from "./data/EmployeeAccessLevel";
import { Employee } from "./data/Employee";
import { IssueScore } from "./data/IssueScore";
import { IssuePriority } from "./data/IssuePriority";
import { Project } from "./data/Project";
import { CustomerEmployee } from "./data/CustomerEmployee";
import { User } from "./data/User";
import { AgentType } from "./data/AgentType";
import { Schedule } from "./data/Schedule";
import { ScheduleDayType } from "./data/ScheduleDayType";
import { ScheduleTemplate } from "./data/ScheduleTemplate";
import { BankAccount } from "./data/BankAccount";
import { Bank } from "./data/Bank";
import { Person } from "./data/Person";
import { BalanceTransaction } from "./data/BalanceTransaction";
import { VacationRequest } from "./data/VacationRequest";

export type WebServiceResponseData = {
    ErrorCode: number,
    ErrorText: string,
    UserReadableError: boolean,
}

type IssueActivityData = {
    ID: number,
    AuthorID: number | null,
    AuthorName: string,
    AuthorAvatar: string,
    ActivityDate: Date,
    Hours: number,
    Comment: string,
    Hidden: boolean,
    IsWorkingHours: boolean,
}

export class API {
    private rootStore: RootStore;

    private api = axios.create({
        baseURL: (import.meta.env.VITE_API_URL ?? '') + "/json/reply/",
        //baseURL: "http://192.168.0.135:8080/json/reply/",
        responseType: "json",
        withCredentials: false,
        adapter: 'fetch',
    })

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
    }

    private post = async (endpoint: string, params?: object, withSavedAuth?: boolean): Promise<AxiosResponse | undefined> => {

        withSavedAuth = withSavedAuth ?? true;

        const headers = new AxiosHeaders({
            "Accept": "application/json",
            "Content-Type": "application/json;charset=utf-8",
        });

        if (withSavedAuth) {
            const token = this.rootStore.userProfile?.Token;

            if ((token ?? '') === '') {
                this.rootStore.routerStore.goTo('login')
                return undefined;
            }

            headers.set("UltimaAuthCookie", token);
        }

        this.rootStore.setLoading(true);
        //await new Promise(resolve => setTimeout(resolve, 3000)); // задержка     
        this.rootStore.globalError.reset();

        return await this.api.post(
            endpoint,
            JSON.stringify(params),
            {
                validateStatus: (status) => status === 200,
                headers: headers,
                auth: !withSavedAuth ? { username: "s-gold", password: "3842" } : undefined,
            }
        )
            .catch((error: AxiosError) => {

                if (withSavedAuth
                    && (error.response?.status === 401 ||
                        (error.response?.status === 500 && (error.response?.data as WebServiceResponseData).ErrorCode == 100))) {
                    this.rootStore.setUserProfile(undefined);
                    if (this.rootStore.routerStore.routerState.routeName != 'login' &&
                        this.rootStore.routerStore.routerState.routeName != 'requestresetpassword' &&
                        this.rootStore.routerStore.routerState.routeName != 'resetpassword') {
                        this.rootStore.goTo('login');
                        return error.response;
                    }
                } else {
                    throw error;
                }
            })
            .finally(() => this.rootStore.setLoading(false));
    }

    private setGlobalError(error: AxiosError) {
        const data = error.response?.data as WebServiceResponseData;
        this.rootStore.globalError.set(data?.ErrorCode ?? error.response?.status ?? 999,
            (data?.UserReadableError === true ? data.ErrorText : 'Web service error. Contact developers')
            ?? error.message)
    }

    signIn = async (login: string, password: string): Promise<boolean> => {

        this.rootStore.setLoading(true);
        this.rootStore.globalError.reset();

        return await this
            .post('Signin', { "Login": login, "PasswordHash": password }, false)
            .then(response => {
                if (typeof response != 'undefined') {
                    const token = (<AxiosResponse>response).headers['ultimaauthcookie'];
                    if (token) {
                        const profile = response.data.UserType === UserType.Employee
                            ? {
                                ID: response.data.AgentID,
                                FirstName: response.data.UserFirstName,
                                LastName: response.data.UserLastName,
                                UserType: response.data.UserType,
                                Token: token,
                                AccessLevelID: response.data.AccessLevelID as EmployeeAccessLevel,
                            } as EmployeeProfile
                            : {
                                ID: response.data.AgentID,
                                FirstName: response.data.UserFirstName,
                                LastName: response.data.UserLastName,
                                UserType: response.data.UserType,
                                Token: token,
                                CustomerID: response.data.CustomerID,
                                AccessLevelID: response.data.AccessLevelID as CustomerEmployeeAccessLevel,
                            } as CustomerEmployeeProfile;

                        this.rootStore.logIn(profile);

                        return true;
                    }
                }

                return false;
            })
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                return false;
            })
    }

    signOut = async () => {
        if (this.rootStore.activityStarted) {
            alert('Не завершена активность');
        } else {
            await this.post('Signout').then(() => {
                this.rootStore.logOut();
            });
        }
    };

    getAvatar = async (agentId: number): Promise<string> => {
        return await this.post('GetAvatar', { AgentID: agentId })
            .then(response => response?.data?.Avatar ?? '' as string ?? '')
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            });
    }

    getIssues = async (filter: IssueListFilterModel, recordsCount?: number):
        Promise<{ Records: IssueListItem[], TotalRecordsCount: number, PageSize: number }> => {
        return await this.post('GetIssues',
            {
                Filter: {
                    AssignedFrom: filter.datesAssigned?.from,
                    AssignedTo: filter.datesAssigned?.to,
                    Assignees: filter.assignees?.map(x => x.ID),
                    Authors: filter.authors?.map(x => x.ID),
                    Clients: filter.clients?.map(x => x.ID),
                    CompletedFrom: filter.datesCompleted?.from,
                    CompletedTo: filter.datesCompleted?.to,
                    CreatedFrom: filter.datesCreate?.from,
                    CreatedTo: filter.datesCreate?.to,
                    QueuedFrom: filter.datesQueued?.from,
                    QueuedTo: filter.datesQueued?.to,
                    DeadLineFrom: filter.datesDeadline?.from,
                    AcceptanceDateFrom: filter.datesAcceptance?.from,
                    AcceptanceDateTo: filter.datesAcceptance?.to,
                    DeadLineTo: filter.datesDeadline?.to,
                    OnlyWithHours: filter.hasFact ?? false,
                    MeMode: filter.meMode ?? false,
                    Projects: filter.projects?.map(x => x.ID),
                    RateFrom: filter.rates?.from,
                    RateTo: filter.rates?.to == 10 ? null : filter.rates?.to,
                    Skills: filter.skills?.map(x => x.ID),
                    States: filter.states,
                    SearchText: filter.text,
                    MyErrors: filter.myErrors,
                },
                RecordsCount: recordsCount ?? 100,
                Offset: filter.offset,
                OrderBy: filter.orderBy

            }).then(response => {

                if (typeof response == 'boolean') {
                    return { TotalRecordsCount: 0, PageSize: 0 } as { Records: IssueListItem[], TotalRecordsCount: number, PageSize: number };
                } else {
                    const recs = (<AxiosResponse>response).data as { Records: IssueListItem[], TotalRecordsCount: number, PageSize: number };
                    return recs;
                }
        });
    }

    getIssue = async (id: number): Promise<Issue | null> => {
        return await this.post('GetIssue', { ID: id })
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            })
            .then(response => {
            if (!response || typeof response.data?.ID !== 'number') {
                return null;
            } else {
                const issue = response.data as Issue;
                /*if (issue.ProjectID) {
                    issue.Project = {
                        ClientID: issue.project.ClientID!,
                        ClientName: issue.Project.ClientName,
                        ID: issue.Project.ID,
                        Name: issue.Project.Name,
                    }
                }*/
                return issue;
            }
        });
    }

    /**
     * Убрать задачу в архив
     * @param {number} id ID задачи
     */
    archiveIssue = async (id: number) => {
        return await this.post('DeleteIssue', { ID: id })
            .then(() => this.rootStore.issuesChannel.emit('onIssueChanged', id!))
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            });
    }

    getIssueScores = async (): Promise<{ ID: number, Name: string, ClientBonus: number, EmployeeBonus: number, Score: number}[]> => {
        return await this.post('GetIssueScores')
            .then(response => response?.data as { ID: number, Name: string, ClientBonus: number, EmployeeBonus: number, Score: number }[])
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            })
    }

    /**
     * Получение проекта
     * @param {number} id ID проекта
     */
    getProject = async (id: number): Promise<Project | null> => {
        return await this
            .post('GetProject', { ID: id })
            .then(response => {
                if (!response || typeof response.data?.ID !== 'number') {
                    return null;
                } else {
                    return {
                        ...response.data, ResponsibleEmployee: { ID: response.data.ResponsibleEmployeeAgentID, Name: response.data.ResponsibleEmployeeAgentName}
                    } as Project;
                }
            })
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            });
    }

    /**
     * Изменение access rules проекта
     * @param {number} projectID ID проекта
     * @param {string} accessRules access rules
     */
    updateProjectAccessRules = async (projectID: number, accessRules:string) => {
        return await this
            .post('UpdateProjectAccessRules', { ProjectID: projectID, AccessRules: accessRules })
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            });
    }

    queueIssue = async (id: number) => {
        return await this
            .post('QueueIssue', { ID: id })
            .then(() => this.rootStore.issuesChannel.emit('onIssueChanged', id!))
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            });
    }

    deleteIssue = async (id: number) => {
        return await this
            .post('DeleteIssue', { ID: id })
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            });
    }

    getIssueComments = async (issueId: number, afterCommentID?: number): Promise<IssueComment[]> => {
        return await this
            .post('GetIssueComments', { IssueID: issueId, AfterCommentID: afterCommentID })
            .then(response => {
                return typeof response !== 'undefined' ? response.data as IssueComment[] : <IssueComment[]>[]
            })
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                return <IssueComment[]>[];
            });
    }

    getIssueAttaches = async (issueId: number): Promise<IssueAttach[]> => {
        return await this
            .post(`GetIssueAttaches`, { IssueID: issueId })
            .then(response => {
                return typeof response !== 'undefined' ? response.data as IssueAttach[] : <IssueAttach[]>[]
            })
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                return <IssueAttach[]>[];
            });
    }

    getIssuePossibleEmloyee = async (issueId: number): Promise<Employee[]> => {
        return await this
            .post('GetIssuePossibleWorkers', { IssueID: issueId })
            .then(response => {
                return !response ? [] :
                    <Employee[]>response!.data;
            })
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                return [];
            });
    }

    getSkilledWorkers = async (skillId: number): Promise<Employee[]> => {
        return await this
            .post('GetSkilledWorkers', { SkillId: skillId })
            .then(response => {
                return !response ? [] :
                    <Employee[]>response!.data;
            })
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                return [];
            });
    }

    getIssuePreferredWorkers = async (issueId: number): Promise<Employee[]> => {
        return await this
            .post('GetIssuePreferredWorkers', { IssueId: issueId })
            .then(response => {
                return !response ? [] :
                    <Employee[]>response!.data
            })
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                return [];
            });
    }

    postIssueComment = async (issueId: number, comment: string, hidden: boolean): Promise<number> => {
        return await this
            .post('AddIssueComment', { IssueID: issueId, Comment: comment, Hidden: hidden })
            .then(response => response?.data.CommentID ?? 0)
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                return 0;
            });
    }

    postIssueAcivity = async (newIssueActivity: NewActivityData): Promise<number> => {
        return await this
            .post('AddTimesheet',
                {
                    IssueID: newIssueActivity.issueId,
                    StartHour: newIssueActivity.timeStart.getHours(),
                    StartMinute: newIssueActivity.timeStart.getMinutes(),
                    StopHour: newIssueActivity.timeEnd.getHours(),
                    StopMinute: newIssueActivity.timeEnd.getMinutes(),
                    SkillID: newIssueActivity.skillId,
                    IsOffDuty: newIssueActivity.isOffDuty,
                    Comment: newIssueActivity.comment,
                    WorkerID: this.rootStore.userProfile?.ID,
                })
            .then(response => {
                const id = response?.data.TimesheetRecordDocumentID ?? 0;
                if (id != 0) {
                    this.getTimesheet(id).then(timesheet =>
                        this.rootStore.timesheetsChannel.emit('onTimesheetAdded', timesheet));
                }
                return id;
            })
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            });
    }

    /**
     * Удаление записанного времени
     * @param {number} id ID докумнта
     */
    deleteTimesheetDocument = async (id: number) => {
        return await this
            .post('DeleteTimesheet', { TimesheetDocumentID: id })
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            });
    }

    assignIssue = async (issueId: number, emploeeAgentId: number) => {
        return await this
            .post('ManualAssignIssue', { IssueId: issueId, EmployeeAgentId: emploeeAgentId, })
            .then(() => this.rootStore.issuesChannel.emit('onIssueChanged', issueId))
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            });
    }

    acceptIssue = async (issueId: number, estimatedHours: number, rejectError?: boolean, rejectReason?: string) => {
        return await this
            .post('AcceptIssue', { IssueId: issueId, EstimatedHours: estimatedHours, RejectError: rejectError ?? null, RejectReason: rejectReason ?? '' })
            .then(() => {
                this.getActiveIssueID().then(id => {
                    const profile = { ...this.rootStore.userProfile as EmployeeProfile };
                    profile.ActiveIssueId = id;
                    this.rootStore.setUserProfile(profile);
                    this.rootStore.issuesChannel.emit('onIssueChanged', issueId); 
                })
            })
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            });
    }

    rejectIssue = async (issueId: number, rejectReason: string) => {
        return await this
            .post('RejectIssue', { IssueId: issueId, RejectReason: rejectReason })
            .then(() => this.rootStore.issuesChannel.emit('onIssueChanged', issueId))
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            });
    }

    testIssue = async (issueId: number) => {
        return await this
            .post('TestIssue', { ID: issueId, })
            .then(() => this.rootStore.issuesChannel.emit('onIssueChanged', issueId))
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            });
    }

    handoverIssueToClient = async (issueId: number) => {
        return await this
            .post('HandoverIssueToClient', { ID: issueId, })
            .then(() => {
                this.getActiveIssueID().then(id => {
                    const profile = { ...this.rootStore.userProfile as EmployeeProfile };
                    profile.ActiveIssueId = id;
                    this.rootStore.setUserProfile(profile);
                    this.rootStore.issuesChannel.emit('onIssueChanged', issueId);
                });
            })
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            });
    }

    completeIssue = async (issueId: number, score: IssueScore, comments: string) => {
        return await this
            .post('CompleteIssue', { ID: issueId, ScoreId: <number>score, Comments: comments })
            .then(() => this.rootStore.issuesChannel.emit('onIssueChanged', issueId))
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            });
    }

    updateIssuePriorityID = async (issueId: number, priorityId: IssuePriority, isError: boolean) => {
        return await this
            .post('UpdateIssuePriorityID',
                {
                    IssueId: issueId,
                    PriorityId: <number>priorityId,
                    IsError: isError,
                })
            .then(() => this.rootStore.issuesChannel.emit('onIssueChanged', issueId))
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            });
    }

    pauseIssue = async (issueId: number, request: string) => {
        return await this
            .post('PauseIssueForComments', { IssueId: issueId, Request: request })
            .then(() => {
                this.getActiveIssueID().then(id => {
                    const profile = { ...this.rootStore.userProfile as EmployeeProfile };
                    profile.ActiveIssueId = id;
                    this.rootStore.setUserProfile(profile);
                    this.rootStore.issuesChannel.emit('onIssueChanged', issueId);
                })
            })
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            });
    }

    resumePausedIssue = async (issueId: number, comments: string) => {
        return await this
            .post('ResumePausedIssue', { IssueId: issueId, Comments: comments })
            .then(() => this.rootStore.issuesChannel.emit('onIssueChanged', issueId))
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            });
    }

    createStubIssue = async (authorId: number, priorityId: number, isError: boolean): Promise<number> => {
        return await this
            .post('CreateStubIssue',
                {
                    AuthorId: authorId,
                    PriorityId: priorityId,
                    IsError: isError,
                })
            .then(response => {
                if (typeof response?.data.ID == 'number') {
                    return response?.data.ID as number
                } else {
                    throw new Error('asdf');
                }
            })
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            });
    }

    getIssueTimesheets = async (issueId: number): Promise<Timesheet[]> => {
        return await this
            .post('GetIssueTimesheets', { IssueID: issueId })
            .then(response => response?.data as Timesheet[])
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                return <Timesheet[]>[];
            });
    }

    getEmployeeHours = async (fromDate: Date, toDate: Date): Promise<number> => {
        return await this
            .post('GetEmployeeHours',
                {
                    FromDate: this.dateWithoutTimezone(fromDate),
                    ToDate: this.dateWithoutTimezone(toDate),
                })
            .then(response => response?.data.WorkerHours as number)
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            });
    }

    getEmployeeVacationRequests = async (employeeID: number): Promise<VacationRequest[]> => {
        return await this
            .post('GetEmployeeVacationRequests', { EmployeeID: employeeID })
            .then(response => response?.data as VacationRequest[])
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            });
    }

    approveVacation = async (vacationDocumentID: number, employeeID: number) => {
        return await this
            .post('ApproveVacation', { VacationDocumentID: vacationDocumentID })
            .then(() => this.rootStore.scheduleChannel.emit('onScheduleChanged', employeeID))
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            });
    }

    getAuthorHours = async (fromDate: Date, toDate: Date):
        Promise<{ ErrorsHours: number, CriticalHours: number, PriorityHours: number, RegularHours: number, TotalHours: number, }> => {
        return await this
            .post('GetAuthorHours',
                {
                    FromDate: this.dateWithoutTimezone(fromDate),
                    ToDate: this.dateWithoutTimezone(toDate),
                })
            .then(response => response?.data as { ErrorsHours: number, CriticalHours: number, PriorityHours: number, RegularHours: number, TotalHours: number, })
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            });
    }

    getQueueLength = async (): Promise<number> => {
        return await this
            .post('GetQueueLength')
            .then(response => response?.data.Length as number)
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            });
    }

    getEmployeeErrorsCount = async (issueCreatedFrom: Date, issueCreatedTo: Date): Promise<number> => {
        return await this
            .post('GetEmployeeErrorsCount',
                {
                    IssueCreatedFrom: this.dateWithoutTimezone(issueCreatedFrom),
                    IssueCreatedTo: this.dateWithoutTimezone(issueCreatedTo),
                })
            .then(response => response?.data.ErrorsCount as number)
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            });
    }

    getTimesheet = async (id: number): Promise<Timesheet> => {
        return await this
            .post('GetTimesheet', { ID: id })
            .then(response => response?.data as Timesheet)
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            });
    }

    getAcivities = async (issueId: number): Promise<IssueActivity[]> => {
        //await new Promise(resolve => setTimeout(resolve, 500000)); // задержка     
        return await this
            .post('GetActivities', { IssueID: issueId })
            .then(response => (response?.data as IssueActivityData[]).map(activity => {
                return {
                    activityType: activity.Hours > 0 ? ActivityType.timesheetRecord : ActivityType.comment,
                    data: activity.Hours > 0
                        ? {
                            Comment: activity.Comment,
                            ID: activity.ID,
                            IssueID: issueId,
                            Hours: activity.Hours,
                            StartDate: moment(activity.ActivityDate).add(activity.Hours, 'hours').toDate(),
                            StopDate: activity.ActivityDate,
                            IsOffDuty: !activity.IsWorkingHours,
                            SkillID: -1, // TODO
                            WorkerID: activity.AuthorID,
                            WorkerName: activity.AuthorName,
                            WorkerAvatar: activity.AuthorAvatar,
                        } as Timesheet : {
                            ID: activity.ID,
                            AuthorID: activity.AuthorID,
                            AuthorName: activity.AuthorName,
                            AuthorAvatar: activity.AuthorAvatar,
                            CommentText: activity.Comment,
                            CreateDate: activity.ActivityDate,
                            Hidden: activity.Hidden,
                            IssueID: issueId,
                        } as IssueComment
                } as IssueActivity
            }))
            /*activity.RawHours > 0
                ? {
                    Comment: activity.Comment,
                    ID: activity.ID,
                    IssueID: issueId,
                    RawHours: activity.RawHours,
                    StartDate: moment(activity.ActivityDate).add(activity.RawHours, 'hours').toDate(),
                    StopDate: activity.ActivityDate,
                    IsOffDuty: !activity.IsWorkingHours,
                    SkillID: -1, // TODO
                    WorkerID: activity.AuthorID,
                    WorkerName: activity.AuthorName,
                } as Timesheet : {
                    ID: activity.ID,
                    AuthorID: activity.AuthorID,
                    AuthorName: activity.AuthorName,
                    CommentText: activity.Comment,
                    CreateDate: activity.ActivityDate,
                    Hidden: activity.Hidden,
                    IssueID: issueId,
                } as IssueComment))*/
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                return [];
            });
    }

    submitIssue = async (issue: Issue) => {
        await this
            .post('SubmitIssue', {
                IssueID: issue.ID,
                ProjectID: issue.Project!.ID,
                Subject: issue.Subject,
                Text: issue.IssueText,
                RequiredSkillID: issue.RequiredSkillID,
                ExternalTrackerLink: issue.ExternalTrackerLink,
            })
            .then(() => {
                if (issue.RequiredSkillID)
                    this.updateIssueSkillID(issue.ID, issue.RequiredSkillID, []);
            })
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            });
    }

    updateIssueText = async (issueId: number, subject: string, text: string) => {
        await this
            .post('UpdateIssueText', { IssueID: issueId, Subject: subject, Text: text  })
            .then(() => this.rootStore.issuesChannel.emit('onIssueChanged', issueId))
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            });
    }

    updateIssueExternalTrackerLink = async (issueId: number, link: string) => {
        await this
            .post('UpdateIssueExternalTrackerLink', { IssueID: issueId, ExternalTrackerLink: link })
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            });
    }

    postIssueResult = async (issueId: number, result: string) => {
        await this
            .post('UpdateIssueResult', { IssueID: issueId, Result: result })
            .then(() => this.rootStore.issuesChannel.emit('onIssueChanged', issueId))
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            });
    }

    dateWithoutTimezone = (date: Date) => {
        const tzoffset = date.getTimezoneOffset() * 60000; //offset in milliseconds
        const withoutTimezone = new Date(date.valueOf() - tzoffset)
            .toISOString()
            .slice(0, -1);
        return withoutTimezone;
    };

    updateIssueEstimatedDate = async (issueId: number, newDate: Date | null, changeReason: string) => {
        await this
            .post('UpdateIssueEstimatedDate', {
                IssueID: issueId,
                NewDate: (newDate ? this.dateWithoutTimezone(newDate) : null),
                UpdateReason: changeReason,
            })
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            });
    }

    updateIssueProjectID = async (issueId: number, projectID: number | null) => {
        await this
            .post('UpdateIssueProjectID', {
                IssueID: issueId,
                ProjectID: (projectID),
            })
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            });
    }

    updateIssueSkillID = async (issueId: number, skillID: number, preferredEmployee: Employee[]) => {
        await this
            .post('UpdateIssueSkillID', {
                IssueID: issueId,
                SkillID: skillID,
                WorkerIDs: preferredEmployee.map(x => x.ID),
            })
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            });
    }

    getClients = async (): Promise<Client[]> => {
        return await this
            .post('GetClients')
            .then(response => (response?.data as
                ((Client & { ResponsibleEmployeeID: number, ResponsibleEmployeeName: string }))[]).map(x =>
                {
                    const c = x as Client;
                    c.ResponsibleEmployee = x.ResponsibleEmployeeID ? { ID: x.ResponsibleEmployeeID, Name: x.ResponsibleEmployeeName} as Employee : undefined;
                    return c;
                }))
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                return <Client[]>[];
            });
    }

    getClient = async (id: number): Promise<Client> => {
        return await this
            .post('GetClient', {ID: id})
            .then(response => {
                const client = response?.data as Client;
                client.ResponsibleEmployee = (!response?.data?.ResponsibleID) ? undefined : { ID: response?.data?.ResponsibleID, Name: response?.data?.ResponsibleName } as Employee;
                client.BankAccounts = [];
                client.ID = id;
                if (response?.data.BankAccount) {
                    client.BankAccounts.push(response?.data.BankAccount as BankAccount);
                    client.BankAccounts[0].Bank = {
                        ID: 0,
                        Name: response?.data.BankAccount.BankName,
                        LegalAddress: response?.data.BankAccount.BankLocation,
                        BIC: response?.data.BankAccount.BIC,
                        CorrAccountNumber: response?.data.BankAccount.CorrAccountNumber
                    } as Bank;
                }

                return client;
            })
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            });
    }

    getClientEmployees = async (clientId: number): Promise<User[]> => {
        return await this
            .post('GetCustomerEmployees', { ClientID: clientId })
            .then(response => (response?.data as ((User & { GenderID: number })[])).map(x => {
                const u = x as User;
                u.Gender = x.GenderID;
                if ((u.Login?.trim() ?? '') == '') {
                    u.AccessLevelID = u.AgentType == AgentType.Employee ? EmployeeAccessLevel.none : CustomerEmployeeAccessLevel.none
                }
                return u;
            }))
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            });
    }

    getClientProjects = async (clientId: number): Promise<Project[]> => {
        return await this
            .post('GetClientProjects', { ClientID: clientId })
            .then(response => response?.data as Project[])
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            });
    }

    getIssueAcivity = async (id: number): Promise<Timesheet | null> => {
        return await this
            .post('GetIssueActivity', { Id: id })
            .then(response => response?.data as Timesheet)
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                return null;
            });
    }

    getActiveIssueID = async (employeeAgentID?: number): Promise<number | null> => {
        return await this
            .post('GetActiveIssueID', { AgentID: employeeAgentID })
            .then(response => response?.data.Id as number | null)
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                return null;
            });
    }

    getSkills = async (): Promise<Skill[]> => {
        return await this
            .post('GetSkills')
            .then(response => response?.data as Skill[])
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            });
    }

    getEmployeeSkills = async (EmployeeAgentID: number): Promise<Skill[]> => {
        return await this
            .post('GetEmployeeSkills', { EmployeeAgentID: EmployeeAgentID })
            .then(response => response?.data as Skill[])
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            });
    }

    /** Получение списка всех наших сотрудников */
    getEmployees = async (agentType?: AgentType): Promise<Person[]> => {
        return await this
            .post('GetEmployees', { AgentTypeID: agentType ?? null })
            .then(response =>
                (response?.data as (Person & { TypeID: number })[])
                    .map(x => { return { ...x, AgentType: x.TypeID as AgentType }; }))
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            });
    }

    /**
     * Получение данных сотрудника
     * @param {number} id ID сотрудника (agentID)
     */
    getEmployee = async (id: number): Promise<Employee | User> => {    
        return await this
            .post('GetEmployee', { ID: id })
            .then(response => {
                if (!response || typeof response.data?.FirstName !== 'string') {
                    this.rootStore.routerStore.goTo('notFound');
                    return {} as Employee;
                } else {
                    const user = response.data as User;
                    user.ID = id;
                    user.AgentType = AgentType.Employee;
                    user.Name = `${user.LastName} ${user.FirstName}`;
                    
                    if (response.data.HeadID) {
                        (user as Employee).Head = { ID: response.data.HeadID, Name: response.data.HeadName } as Employee;
                    }

                    if (response.data.TrackerLogin ?? '' != '') {
                        user.Login = response.data.TrackerLogin;
                        user.AccessLevelID = response.data.AccessLevelID as EmployeeAccessLevel;
                    }
                    return user.Login ?? '' != '' ? user : user as Employee;
                }
            })
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            });
    }

    /**
     * Получение текущего баланса сотрудника
     */
    getMyBalance = async (): Promise<number> => {
        return await this
            .post('GetMyBalance')
            .then(response => (response?.data?.CurrentBalance ?? 0) as number)
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            });
    }

    /**
     * Получение транзакций текущего сотрудника
     */
    getMyBalanceTransactions = async (fromDate: Date, toDate: Date): Promise<BalanceTransaction[]> => {
        return await this
            .post('GetMyBalanceTransactions', { FromDate: fromDate, ToData: toDate })
            .then(response => response?.data as BalanceTransaction[])
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            });
    };

    /**
     * Получение баланса отпуска текущего пользователя
     */
    getMyVacationBalance = async (): Promise<number> => {
        return await this
            .post('GetVacationBalance')
            .then(response => response?.data.AccruedDaysCount as number)
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            });
    };

    /**
     * Получение баланса отпуска текущего пользователя
     */
    getVacationBalance = async (employeeID: number): Promise<number> => {
        return await this
            .post('GetVacationBalance', { EmployeeID: employeeID })
            .then(response => response?.data.AccruedDaysCount as number)
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            });
    };

    /**
     * Получение данных сотрудника
     * @param {number} id ID сотрудника (agentID)
     */
    getSharedEmployeeProfile = async (id: number): Promise<Employee | CustomerEmployee | User> => {
        return await this
            .post('GetSharedEmployeeProfile', { ID: id })
            .then(response => {
                if (!response || (response.data?.TypeID !== AgentType.Employee && response.data?.TypeID !== AgentType.CustomerEmployee)) {
                    this.rootStore.routerStore.goTo('notFound');
                    return {} as Employee;
                } else {
                    const user = response.data?.TypeID == AgentType.Employee ? response.data.EmployeeData as User : response.data.CustomerEmployeeData as User;
                    user.ID = id;
                    user.Name = (user as Employee).Name ?? (user as CustomerEmployee).Name;
                    user.Gender = response.data.EmployeeData?.GenderID ?? response.data.CustomerEmployeeData?.GenderID;

                    if (response.data.EmployeeData?.HeadID) {
                        (user as Employee).Head = { ID: response.data.EmployeeData.HeadID, Name: response.data.EmployeeData.HeadName } as Employee;
                    }

                    user.AgentType = response.data?.TypeID;

                    if (response.data.CustomerEmployeeData) {
                        (user as CustomerEmployee).Customer = { ID: response.data.CustomerEmployeeData.CustomerID, Name: response.data.CustomerEmployeeData.CustomerName } as Client;
                    }

                    if (response.data.EmployeeData?.TrackerLogin ?? response.data.CustomerEmployeeData?.TrackerLogin ?? '' != '') {
                        user.Login = response.data.EmployeeData?.TrackerLogin ?? response.data.CustomerEmployeeData?.TrackerLogin;
                        user.AccessLevelID =
                            response.data.EmployeeData
                                ? response.data.EmployeeData.AccessLevelID as EmployeeAccessLevel
                                : response.data.CustomerEmployeeData.AccessLevelID as CustomerEmployeeAccessLevel
                    }
                    return user.Login ?? '' != '' ? user : user as Employee;
                }
            })
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            });
    }

    /**
     * Получение шаблона расписания сотрудника
     * @param {number} employeeAgentId ID сотрудника (agentID)
     */
    getScheduleTemplate = async (employeeAgentId?: number): Promise<ScheduleTemplate[]> => {
        return await this
            .post('GetEmployeeScheduleTemplate', { AgentID: employeeAgentId ?? this.rootStore.userProfile?.ID })
            .then(response => {
                if (response) {
                    return (response.data).map((x: {
                        WeekDayID: number,
                        H0: boolean, H1: boolean, H2: boolean, H3: boolean, H4: boolean, H5: boolean,
                        H6: boolean, H7: boolean, H8: boolean, H9: boolean, H10: boolean, H11: boolean,
                        H12: boolean, H13: boolean, H14: boolean, H15: boolean, H16: boolean, H17: boolean,
                        H18: boolean, H19: boolean, H20: boolean, H21: boolean, H22: boolean, H23: boolean,
                    }) => <ScheduleTemplate>{
                        weekDay: x.WeekDayID == 7 ? 0 : x.WeekDayID,
                        hours: [x.H0, x.H1, x.H2, x.H3, x.H4, x.H5, x.H6, x.H7, x.H8, x.H9, x.H10, x.H11, x.H12,
                        x.H13, x.H14, x.H15, x.H16, x.H17, x.H18, x.H19, x.H20, x.H21, x.H22, x.H23],
                    });
                }
                else
                    return [];
            })
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            });
    }

    /**
     * Сохранение шаблона расписания сотрудника
     * @param {number} employeeAgentId ID сотрудника (agentID)
     */
    updateScheduleTemplate = async (template: ScheduleTemplate[]) => {
        const t = template.map(x => { return { ...x, WeekDayID: (x.weekDay == 0 ? 7 : x.weekDay) } });
        return await this
            .post('SetEmployeeScheduleTemplate', {
                DayTemplate: t
            })
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            });
    }

    /**
     * Получение расписания сотрудника
     * @param {number} employeeAgentId ID сотрудника (agentID)
     * @param {Date} dateFrom с какой даты
     * @param {Date} dateTo по какую дату
     */
    getSchedule = async (employeeAgentId: number, dateFrom: Date, dateTo: Date): Promise<Schedule[]> => {
        return await this
            .post('GetEmployeeSchedule', { AgentID: employeeAgentId, DateFrom: dateFrom, DateTo: dateTo })
            .then(response => {
                if (response) {
                    return (response.data).map((x: {
                        Date: Date, DayStatusID: number,
                        H0: boolean, H1: boolean, H2: boolean, H3: boolean, H4: boolean, H5: boolean,
                        H6: boolean, H7: boolean, H8: boolean, H9: boolean, H10: boolean, H11: boolean,
                        H12: boolean, H13: boolean, H14: boolean, H15: boolean, H16: boolean, H17: boolean,
                        H18: boolean, H19: boolean, H20: boolean, H21: boolean, H22: boolean, H23: boolean,
                    }) => <Schedule>{
                            Date: x.Date,
                            DayType: x.DayStatusID,
                            Hours: [x.H0, x.H1, x.H2, x.H3, x.H4, x.H5, x.H6, x.H7, x.H8, x.H9, x.H10, x.H11, x.H12,
                            x.H13, x.H14, x.H15, x.H16, x.H17, x.H18, x.H19, x.H20, x.H21, x.H22, x.H23],
                    });
                }
                else
                    return [];
            })
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            });
    }

    /**
     * Изменение расписания сотрудника
     * @param {number} employeeAgentId ID сотрудника (agentID)
     * @param {Date} date дата
     */
    setSchedule = async (date: Date, hours: boolean[]) => {
        return await this
            .post('SetEmployeeSchedule', {
                AgentID: this.rootStore.userProfile?.ID,
                DayStatusID: ScheduleDayType.working,
                Date: date,
                H0: hours[0],
                H1: hours[1],
                H2: hours[2],
                H3: hours[3],
                H4: hours[4],
                H5: hours[5],
                H6: hours[6],
                H7: hours[7],
                H8: hours[8],
                H9: hours[9],
                H10: hours[10],
                H11: hours[11],
                H12: hours[12],
                H13: hours[13],
                H14: hours[14],
                H15: hours[15],
                H16: hours[16],
                H17: hours[17],
                H18: hours[18],
                H19: hours[19],
                H20: hours[20],
                H21: hours[21],
                H22: hours[22],
                H23: hours[23],
            })
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            });
    }

    /**
     * Заполнить расписание сотрудника на указанный период по шаблону 
     * @param {Date} fromDate начало периода
     * @param {Date} toDate конец периода
     */
    fillEmployeeScheduleByTemplate = async (fromDate: Date, toDate: Date) => {
        return await this
            .post('FillEmployeeScheduleByTemplate', { StartDate: fromDate, Enddate: toDate })
            .then(() => {
                if (this.rootStore.userProfile) {
                    this.rootStore.scheduleChannel.emit('onScheduleChanged', this.rootStore.userProfile.ID);
                }
            })
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            });
    }

    /**
     * Запрос отпуска
     * @param {Date[]} vacationDates запрашиваемые даты отпуска
     */
    requestVacation = async (vacationDates: Date[]) => {
        return await this
            .post('RequestVacation', { VacationDates: vacationDates})
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            });
    }

    /**
     *  Обновление данных сотрудника
     * @param {Employee} employee данные сотрудника
     */
    updateEmployee = async (employee: Employee) => {
        return await this
            .post('UpdateEmployeeProfile',
                {   
                    FirstName: employee.FirstName,
                    MiddleName: employee.MiddleName,
                    LastName: employee.LastName,

                    Phone: employee.Phone,
                    Skype: employee.Skype,
                    Telegram: employee.Telegram,
                    Email: employee.Email,
                    
                    BirthDate: employee.BirthDate,
                    GenderID: <number>employee.Gender,
                    INN: employee.Inn,

                    IssuePreferredRate: employee.IssuePreferredRate,

                    PaymentBankAccount: employee.PaymentBankAccount,
                    PaymentVATNo: employee.PaymentVATno,
                    PaymentBIC: employee.PaymentBIC,
                    PaymentCorrAccNo: employee.PaymentCorrAccNo,
                    PaymentName: employee.PaymentName,
                    PaymentBankName: employee.PaymentBankName,
                    PaymentBankLocation: employee.PaymentBankLocation,

                    Avatar: employee.Avatar,
                })
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            });
    }

    /**
     *  Обновление данных сотрудника клиента
     * @param {CustomerEmployee} employee данные сотрудника
     */
    updateCustomerEmployee = async (employee: CustomerEmployee | User) => {
        return await this
            .post('UpdateCustomerEmployeeProfile',
                {
                    AgentID: employee.ID,
                    FirstName: employee.FirstName,
                    MiddleName: employee.MiddleName,
                    LastName: employee.LastName,

                    Phone: employee.Phone,
                    Skype: employee.Skype,
                    Telegram: employee.Telegram,
                    Email: employee.Email,

                    TrackerLogin: (employee as User).Login,
                    AccessLevelID: (employee as User).AccessLevelID,

                    GenderID: <number>employee.Gender,
                })
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            });
    }

    /**
     *  Обновление данных клиента
     * @param {Client} employee данные клиента
     */
    updateClient = async (client: Client) => {
        return await this
            .post('UpdateClient',
                {
                    ID: client.ID,
                    Website: client.ID,
                    Phone: client.Phone,
                    ActualAddress: client.ActualAddress,
                    Email: client.Email,
                    Inn: client.Inn,
                    Kpp: client.Kpp,
                    OKONH: client.OKONH,
                    LegalAddress: client.LegalAddress,
                    OKPO: client.OKPO,
                    PostAddress: client.PostAddress,
                    BankAccount: {
                        AccountNumber: client.BankAccounts[0].AccountNumber,
                        BIC: client.BankAccounts[0].Bank.BIC,
                        CorrAccountNumber: client.BankAccounts[0].Bank.CorrAccountNumber,
                        BankName: client.BankAccounts[0].Bank.FullName,
                        BankLocation: client.BankAccounts[0].Bank.LegalAddress,
                        Inn: client.BankAccounts[0].Inn,
                        Kpp: client.BankAccounts[0].Kpp,
                        RecipientName: client.BankAccounts[0].RecipientName,
                    }
                })
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            });
    }

    /**
     *  Обновление данных сотрудника клиента
     * @param {CustomerEmployee} employee данные сотрудника
     */
    createCustomerEmployee = async (employee: CustomerEmployee | User): Promise<CustomerEmployee | User> => {
        return await this
            .post('CreateCustomerEmployeeProfile',
                {
                    FirstName: employee.FirstName,
                    MiddleName: employee.MiddleName,
                    LastName: employee.LastName,

                    Phone: employee.Phone,
                    Skype: employee.Skype,
                    Telegram: employee.Telegram,
                    Email: employee.Email,

                    TrackerLogin: (employee as User).Login,
                    AccessLevelID: (employee as User).AccessLevelID as CustomerEmployeeAccessLevel,

                    GenderID: <number>employee.Gender,
                })
            .then(async response => {
                const id = response?.data.ID as number;
                return this.getSharedEmployeeProfile(id).then(e => e as (CustomerEmployee | User));
            })
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            });
    }

    updateEmployeePaymentDetails = async (employee: Employee) => {
        return await this
            .post('UpdateEmployeePaymentDetails',
                {
                    EmployeeAgentID: employee.ID,
                    IssuePreferredRate: employee.IssuePreferredRate,
                    PaymentBankAccNo: employee.PaymentBankAccount,
                    PaymentBankName: employee.PaymentBankName,
                    PaymentBIC: employee.PaymentBIC,
                    PaymentCorrBankAccNo: employee.PaymentCorrAccNo,
                    PaymentName: employee.PaymentName,
                    PaymentVATNo: employee.PaymentVATno,
                    PaymentPurposeDefaultTemplate: employee.PaymentPurposeDefaultTemplate,
                })
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            });
    }
    
    /**
     * Получение списка виновников ошибки
     * @param {number} issueId ID задачи
     */
    getIssueErrorCausers = async (issueId: number): Promise<Employee[]> => {
        return await this
            .post('GetIssueIssueErrorCausers', {IssueID: issueId})
            .then(response => response?.data as Employee[])
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            });
    }

    /**
     * Обновление списка виновников ошибки
     * @param {number} issueId ID задачи
     * @param {Employee[]} causers список виновников
     * @param {string} comments комментарии
     */
    updateIssueErrorCausers = async (issueId: number, causers: Employee[], comments: string) => {
        return await this
            .post('UpdateIssueErrorCausers',
                {
                    IssueID: issueId,
                    WorkerIDs: causers.map(x => x.ID),
                    Comments: comments
                })
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            });
    }

    resetPassword = async (newPasswordHash: string) => {
        await this
            .post(`ResetPassword`, {
                "RequestHash": this.rootStore.routerStore.routerState.queryParams['hash'],
                "NewPasswordHash": newPasswordHash,
                "TypeID": this.rootStore.routerStore.routerState.queryParams['typeID'],
                "RecID": this.rootStore.routerStore.routerState.queryParams['recID'],
            }, false)
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            })
    }

    requestResetPassword = async (login: string): Promise<boolean> => {
        return await this
            .post(`RequestPasswordReset`, {
                "login": login,
            }, false)
            .then(() => {
                return true;
            })
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                return false;
            })
    }

    /**
     * Добавление приложения к задаче
     * @param {number} issueId ID задачи
     * @param {File} content файл
     * @param {string} fileName имя файла
     * @returns {string} путь к добавленному аттачу
     */
    uploadIssueAttach = async (issueId: number, content: File, fileName: string): Promise<string> => {
        const buffer = await content.arrayBuffer();
        const byteArray = new Uint8Array(buffer);
        const a = [...byteArray];

        return await this
            .post(`AddIssueAttach`, {
                IssueID: issueId,
                FileName: fileName,
                Content: a,
            })
            .then(response => {
                const data = response?.data as IssueAttach;
                return /*(import.meta.env.VITE_API_URL ?? '') + */ `/json/reply/GetIssueAttach?ID=${data.ID}&hash=${data.Hash}`;
            })
            .catch((error: AxiosError) => {
                this.setGlobalError(error);
                throw error;
            })
    }
}

